/**
 * This file was auto-generated.
 * Do not make direct changes to the file.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { z } from 'zod'

import * as AdminProductsTypes from './admin-products-types'
import * as CommonTypes from './common-types'

export const PromotionScheduleStatus = {
	Active: 'ACTIVE',
	Scheduled: 'SCHEDULED',
	Expired: 'EXPIRED',
	Inactive: 'INACTIVE'
} as const
export type PromotionScheduleStatusType = (typeof PromotionScheduleStatus)[keyof typeof PromotionScheduleStatus]
export type PromotionListItem = {
	uuid: string
	name: string
	displayTitle?: string
	salesChannels: string[]
	startDate?: number
	endDate?: number
	enabled: boolean
	rank: number
}
export const DiscountType = {
	Percentage: 'PERCENTAGE',
	FixedAmount: 'FIXED_AMOUNT'
} as const
export type DiscountTypeType = (typeof DiscountType)[keyof typeof DiscountType]
export type FixedAmountDiscountDefinition = {
	type: 'FIXED_AMOUNT'
	amount: CommonTypes.Amount
	currencyCode: string
}
export type PercentageDiscountDefinition = {
	type: 'PERCENTAGE'
	percentage: CommonTypes.Amount
}
export type DiscountDefinition = FixedAmountDiscountDefinition | PercentageDiscountDefinition
export const ApplicationRuleType = {
	XItemsFromYProductGroups: 'X_ITEMS_FROM_Y_PRODUCT_GROUPS',
	XItemsOfSameVariant: 'X_ITEMS_OF_SAME_VARIANT',
	BuyXGetY: 'BUY_X_GET_Y'
} as const
export type ApplicationRuleTypeType = (typeof ApplicationRuleType)[keyof typeof ApplicationRuleType]
export type ApplicationRuleEntry =
	| {
			productGroupUuid: string
			quantity: number
	  }
	| {
			productCategoryUuid: string
			quantity: number
	  }
export type ApplicationRule =
	| {
			type: 'X_ITEMS_FROM_Y_PRODUCT_GROUPS'
			products: ApplicationRuleEntry[]
	  }
	| {
			type: 'X_ITEMS_OF_SAME_VARIANT'
			productGroupUuid: string
			quantity: number
	  }
	| {
			type: 'BUY_X_GET_Y'
			qualifying: ApplicationRuleEntry[]
			benefit: (
				| {
						productGroupUuid: string
						quantity: number
						discount: DiscountDefinition
				  }
				| {
						productCategoryUuid: string
						quantity: number
						discount: DiscountDefinition
				  }
			)[]
	  }
export const DayOfWeek = {
	Monday: 'MONDAY',
	Tuesday: 'TUESDAY',
	Wednesday: 'WEDNESDAY',
	Thursday: 'THURSDAY',
	Friday: 'FRIDAY',
	Saturday: 'SATURDAY',
	Sunday: 'SUNDAY'
} as const
export type DayOfWeekType = (typeof DayOfWeek)[keyof typeof DayOfWeek]
export type TimeOfDay = {
	hour: number
	minute: number
	second: number
}
export type PromotionDayOfWeek = {
	dayOfWeek: DayOfWeekType
	activeFromTime?: TimeOfDay
	activeToTime?: TimeOfDay
}
export type Promotion = {
	uuid: string
	name: string
	displayTitle?: string
	externalId?: string
	salesChannels: {
		uuid: string
		name: string
	}[]
	startDate?: number
	endDate?: number
	discountDefinition?: DiscountDefinition
	applicationRule?: ApplicationRule
	prices: AdminProductsTypes.Price[]
	includeFees: boolean
	shouldOverridePrice: boolean
	applyToAllProducts: boolean
	productGroupUuids: string[]
	productUuids: string[]
	productCategoryUuids: string[]
	daysOfWeek?: PromotionDayOfWeek[]
	enabled: boolean
	rank: number
}

export const zPromotionScheduleStatus = z.enum(['ACTIVE', 'SCHEDULED', 'EXPIRED', 'INACTIVE'])
export const zPromotionListItem = z.object({
	uuid: z.string(),
	name: z.string(),
	displayTitle: z.string().optional(),
	salesChannels: z.array(z.string()),
	startDate: z.number().optional(),
	endDate: z.number().optional(),
	enabled: z.boolean(),
	rank: z.number().int().min(0)
})
export const zDiscountType = z.enum(['PERCENTAGE', 'FIXED_AMOUNT'])
export const zFixedAmountDiscountDefinition = z.object({
	type: z.enum(['FIXED_AMOUNT']),
	amount: CommonTypes.zAmount,
	currencyCode: z.string()
})
export const zPercentageDiscountDefinition = z.object({
	type: z.enum(['PERCENTAGE']),
	percentage: CommonTypes.zAmount
})
export const zDiscountDefinition = z.discriminatedUnion('type', [
	zFixedAmountDiscountDefinition,
	zPercentageDiscountDefinition
])
export const zApplicationRuleType = z.enum(['X_ITEMS_FROM_Y_PRODUCT_GROUPS', 'X_ITEMS_OF_SAME_VARIANT', 'BUY_X_GET_Y'])
export const zApplicationRuleEntry = z.union([
	z.object({
		productGroupUuid: z.string(),
		quantity: z.number()
	}),
	z.object({
		productCategoryUuid: z.string(),
		quantity: z.number()
	})
])
export const zApplicationRule = z.union([
	z.object({
		type: z.enum(['X_ITEMS_FROM_Y_PRODUCT_GROUPS']),
		products: z.array(zApplicationRuleEntry)
	}),
	z.object({
		type: z.enum(['X_ITEMS_OF_SAME_VARIANT']),
		productGroupUuid: z.string(),
		quantity: z.number()
	}),
	z.object({
		type: z.enum(['BUY_X_GET_Y']),
		qualifying: z.array(zApplicationRuleEntry),
		benefit: z.array(
			z.union([
				z.object({
					productGroupUuid: z.string(),
					quantity: z.number(),
					discount: zDiscountDefinition
				}),
				z.object({
					productCategoryUuid: z.string(),
					quantity: z.number(),
					discount: zDiscountDefinition
				})
			])
		)
	})
])
export const zDayOfWeek = z.enum(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'])
export const zTimeOfDay = z.object({
	hour: z.number(),
	minute: z.number(),
	second: z.number()
})
export const zPromotionDayOfWeek = z.object({
	dayOfWeek: zDayOfWeek,
	activeFromTime: zTimeOfDay.optional().nullable(),
	activeToTime: zTimeOfDay.optional().nullable()
})
export const zPromotion = z.object({
	uuid: z.string(),
	name: z.string(),
	displayTitle: z.string().optional(),
	externalId: z.string().optional(),
	salesChannels: z.array(
		z.object({
			uuid: z.string(),
			name: z.string()
		})
	),
	startDate: z.number().optional(),
	endDate: z.number().optional(),
	discountDefinition: zDiscountDefinition.optional().nullable(),
	applicationRule: zApplicationRule.optional().nullable(),
	prices: z.array(AdminProductsTypes.zPrice),
	includeFees: z.boolean(),
	shouldOverridePrice: z.boolean(),
	applyToAllProducts: z.boolean(),
	productGroupUuids: z.array(z.string()),
	productUuids: z.array(z.string()),
	productCategoryUuids: z.array(z.string()),
	daysOfWeek: z.array(zPromotionDayOfWeek).optional(),
	enabled: z.boolean(),
	rank: z.number().int().min(0)
})
